import * as v from 'valibot'

import { Polygon, MultiPolygon, LineString, MultiLineString } from './geojson'
import ValibotCommon, { TimezoneIANASchema } from './common'


export const EDGE_GROUPING_CRITERIA = {
	SPEED_LIMIT_AND_ROAD_NAME:'speed_limit_and_road_name',
	MAINLINE:'mainline',
	ROAD_WORKS:'road_works',
	BIDIRECTIONAL_SPEED_LIMIT_AND_ROAD_NAME_AND_FRC:'bidirectional_speed_limit_and_road_name_and_frc',
	USER_DEFINED_ROUTE:'user_defined_route',
	MRCOG:'mrcog',
} as const
export type EDGE_GROUPING_CRITERIA = typeof EDGE_GROUPING_CRITERIA[keyof typeof EDGE_GROUPING_CRITERIA]
export const EDGE_GROUPING_CRITERIA_SCHEMA = v.picklist(Object.values(EDGE_GROUPING_CRITERIA) as EDGE_GROUPING_CRITERIA[])

export const TOMTOM_FUNCTIONAL_ROAD_CLASS_TYPE = {
	LOCAL_CONNECTING_ROADS: 'Local Connecting Roads',
	LOCAL_ROADS:'Local Roads',
	LOCAL_ROADS_OF_HIGH_IMPORTANCE:'Local Roads of High Importance',
	LOCAL_ROADS_OF_MINOR_IMPORTANCE:'Local Roads of Minor Importance',
	MAJOR_ROADS_LESS_IMPORTANT_THAN_MOTORWAYS:'Major Roads less important than Motorways',
	MOTORWAYS_FREEWAYS_MAJOR_ROADS:'Motorways; Freeways; Major Roads',
	OTHER_MAJOR_ROADS:'Other Major Roads',
	OTHER_ROADS:'Other Roads',
	SECONDARY_ROADS:'Secondary Roads',
}
export type TOMTOM_FUNCTIONAL_ROAD_CLASS_TYPE = typeof TOMTOM_FUNCTIONAL_ROAD_CLASS_TYPE[keyof typeof TOMTOM_FUNCTIONAL_ROAD_CLASS_TYPE]
export const TOMTOM_FUNCTIONAL_ROAD_CLASS_TYPE_SCHEMA = v.picklist(Object.values(TOMTOM_FUNCTIONAL_ROAD_CLASS_TYPE) as TOMTOM_FUNCTIONAL_ROAD_CLASS_TYPE[])


export const phase_number_schema = v.pipe(v.number(), v.integer(), v.finite(), v.minValue(1))

export function getCustomerBaseSchema({
	uuid_params,
}: {
	uuid_params: {
		expected_format: 'short' | 'full',
		convert_format: boolean,
	},
}) {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'id',
		}),
		name: v.string(),
		valid_from: v.nullish(v.string()),
		valid_to: v.nullish(v.string()),
	})
}

export function getRegionBaseSchema({
	uuid_params,
}: {
	uuid_params: {
		expected_format: 'short' | 'full',
		convert_format: boolean,
	},
}) {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'id',
		}),
		customer_id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'customer_id',
		}),
		name: v.string(),
		gps: v.variant('type', [Polygon, MultiPolygon]),
		timezone_iana: v.nullish(TimezoneIANASchema),
		valid_from: v.nullish(v.string()),
		valid_to: v.nullish(v.string()),
	})
}

export function getNetworkBaseSchema({
	uuid_params,
}: {
	uuid_params: {
		expected_format: 'short' | 'full',
		convert_format: boolean,
	},
}) {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'id',
		}),
		region_id: v.nullish(ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'region_id',
		})),
		customer_id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'customer_id',
		}),
		name: v.string(),
		name_short: v.nullish(v.string()),
		gps: v.variant('type', [Polygon, MultiPolygon]),
		timezone_iana: v.nullish(TimezoneIANASchema),
		valid_from: v.nullish(v.string()),
		valid_to: v.nullish(v.string()),
		enabled_in_sig_ops_mode: v.nullish(v.boolean()),
		enabled_in_safety_mode: v.nullish(v.boolean()),
		is_user_generated: v.boolean(),
	})
}

export function getSignalBaseSchema({
	uuid_params,
}: {
	uuid_params: {
		expected_format: 'short' | 'full',
		convert_format: boolean,
	},
}) {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'id',
		}),
		external_signal_id: v.nullish(v.string()),
		customer_id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'customer_id',
		}),
		name: v.nullish(v.string()),
		name_short: v.nullish(v.string()),
		timezone_iana: TimezoneIANASchema,
		valid_from: v.nullish(v.string()),
		valid_to: v.nullish(v.string()),
		atspm_route_ids: v.array(
			ValibotCommon.getUuidSchema_V2({
				expected_format: uuid_params.expected_format,
				convert_format: uuid_params.convert_format,
				error_field_name: 'atspm_route_id',
			}),
		),
		controller_ip_address: v.nullish(v.string()),
	})
}

export function getIntersectionBaseSchema({
	uuid_params,
}: {
	uuid_params: {
		expected_format: 'short' | 'full',
		convert_format: boolean,
	},
}) {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'id',
		}),
		signal_id: v.nullish(ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'signal_id',
		})),
		name: v.nullish(v.string()),
		name_short: v.nullish(v.string()),
		gps: Polygon,
		valid_from: v.nullish(v.string()),
		valid_to: v.nullish(v.string()),
		is_modelled: v.nullish(v.boolean()),
		is_problematic: v.nullish(v.boolean()),
		is_missing_tomtom_approach: v.nullish(v.boolean()),
		has_protected_right_turn: v.nullish(v.boolean()),
		is_signalized: v.nullish(v.boolean()),
		user_id: v.nullish(ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'atspm_route_id',
		})),
	})
}

export function getRouteLegacyBaseSchema({
	uuid_params,
}: {
	uuid_params: {
		expected_format: 'short' | 'full',
		convert_format: boolean,
	},
}) {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'id',
		}),
		customer_id: v.nullish(ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'customer_id',
		})),
		network_id: v.nullish(ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'network_id',
		})),
		edge_ids: v.array(ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'edge_id',
		})),
		gps: LineString,
		name: v.nullish(v.string()),
		name_short: v.nullish(v.string()),
		valid_from: v.nullish(v.string()),
		valid_to: v.nullish(v.string()),
		is_primary: v.nullish(v.boolean()),
		is_user_generated: v.boolean(),
	})
}

export function getEdgeGroupBaseSchema({
	uuid_params,
}: {
	uuid_params: {
		expected_format: 'short' | 'full',
		convert_format: boolean,
	},
}) {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'id',
		}),
		customer_id: v.nullish(ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'customer_id',
		})),
		network_id: v.nullish(ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'network_id',
		})),
		edge_ids: v.array(ValibotCommon.getUuidSchema_V2({
			expected_format: uuid_params.expected_format,
			convert_format: uuid_params.convert_format,
			error_field_name: 'edge_id',
		})),
		gps: v.variant('type', [LineString, MultiLineString]),
		grouping_criteria: EDGE_GROUPING_CRITERIA_SCHEMA,
		name: v.nullish(v.string()),
		name_short: v.nullish(v.string()),
		valid_from: v.nullish(v.string()),
		valid_to: v.nullish(v.string()),
		start_road_name: v.nullish(v.string()),
		end_road_name: v.nullish(v.string()),
		speed_limit_meters_per_second: v.nullish(v.number()),
		tomtom_road_type: v.nullish(TOMTOM_FUNCTIONAL_ROAD_CLASS_TYPE_SCHEMA),
		tomtom_frc_number: v.nullish(v.number()),
		number_of_lanes: v.nullish(v.number()),
		length_meters: v.nullish(v.number()),
		is_user_generated: v.boolean(),
	})
}
